/**
 * Curva em tabela e gráfico (bomba: vazão × altura; rendimento; curva de volume) — Nexora Grid (frontend, US-406).
 *
 * Criado em: 16/10/2026 · Última modificação: 16/10/2026 · Versão: 1.0.0
 */
import type { Campo } from "./tipos";

type Ponto = Record<string, number | string>;

export function Curva({ campo, pontos, editavel, aoMudar }: {
  campo: Campo; pontos: Ponto[]; editavel: boolean; aoMudar: (p: Ponto[]) => void;
}) {
  const [cx, cy] = campo.colunas!;
  const numeros = pontos.map((p) => ({ x: Number(p[cx[0]]), y: Number(p[cy[0]]) })).filter((p) => !isNaN(p.x) && !isNaN(p.y));
  const maxX = Math.max(1, ...numeros.map((p) => p.x)), maxY = Math.max(1, ...numeros.map((p) => p.y));
  const L = 260, A = 140, M = 30;
  const px = (x: number) => M + (x / maxX) * (L - M - 8);
  const py = (y: number) => A - M + 8 - (y / maxY) * (A - M);
  const mudar = (i: number, chave: string, valor: string) =>
    aoMudar(pontos.map((p, k) => (k === i ? { ...p, [chave]: valor === "" ? "" : Number(valor) } : p)));
  return (
    <div className="curva">
      <table className="tabela tabela-curva">
        <thead><tr><th>{cx[1]}</th><th>{cy[1]}</th>{editavel && <th aria-label="ações" />}</tr></thead>
        <tbody>
          {pontos.map((p, i) => (
            <tr key={i}>
              {[cx[0], cy[0]].map((c) => (
                <td key={c}>{editavel
                  ? <input type="number" step="any" aria-label={`${c} ponto ${i + 1}`} value={p[c] ?? ""}
                    onChange={(e) => mudar(i, c, e.target.value)} />
                  : <span className="nx-valor">{String(p[c])}</span>}</td>
              ))}
              {editavel && <td><button type="button" className="nx-botao" aria-label={`remover ponto ${i + 1}`}
                onClick={() => aoMudar(pontos.filter((_, k) => k !== i))}>✕</button></td>}
            </tr>
          ))}
        </tbody>
      </table>
      {editavel && <button type="button" className="nx-botao" onClick={() => aoMudar([...pontos, { [cx[0]]: "", [cy[0]]: "" }])}>
        + ponto</button>}
      <svg viewBox={`0 0 ${L} ${A}`} className="grafico-curva" role="img" aria-label={`Gráfico: ${campo.rotulo}`}>
        <line x1={M} y1={A - M + 8} x2={L - 8} y2={A - M + 8} className="eixo" />
        <line x1={M} y1={8} x2={M} y2={A - M + 8} className="eixo" />
        <text x={L - 8} y={A - 6} textAnchor="end" className="rotulo-eixo">{cx[1]}</text>
        <text x={4} y={14} className="rotulo-eixo">{cy[1]}</text>
        {numeros.length > 1 && <polyline points={numeros.map((p) => `${px(p.x)},${py(p.y)}`).join(" ")} className="linha-curva" />}
        {numeros.map((p, i) => <circle key={i} cx={px(p.x)} cy={py(p.y)} r={3} className="ponto-curva" />)}
      </svg>
    </div>
  );
}
